import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ShieldAlert, HardHat, Building2, House, Hammer } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ExpertStructureRemoval() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const services = [{
    id: "residential",
    title: "RESIDENTIAL DEMO & HAULING",
    badge: "ESTATE CLEARANCE",
    icon: <House className="w-5 h-5" />,
    description: "Complete structural removal for single-family homes and multi-unit properties. SMITH & B GROUP LLC manages the entire process from debris hauling to final site clearing, ensuring a clean slate for your new build.",
    features: ["Total structure teardown", "Debris hauling & disposal", "Foundation & slab removal", "Lot clearing & grading"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1622208/img/residential_demolition_house.png",
    link: "/ServicesPage"
  }, {
    id: "commercial",
    title: "PROJECT MANAGEMENT & PREP",
    badge: "COMMERCIAL GRADE",
    icon: <Building2 className="w-5 h-5" />,
    description: "Heavy-duty site preparation and logistics for commercial and industrial facilities. Our team coordinates complex teardowns with precision project management to protect surrounding infrastructure and timelines.",
    features: ["Site logistics & coordination", "Concrete crushing & removal", "Pressure cleaning services", "Material salvage & recycling"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1622208/img/commercial_demolition_site.png",
    link: "/ServicesPage"
  }, {
    id: "interior",
    title: "INTERIOR STRIP-OUTS",
    badge: "SELECTIVE DEMO",
    icon: <Hammer className="w-5 h-5" />,
    description: "Surgical demolition for renovation projects. We strip interiors down to the studs—including drywall removal and flooring stripping—while preserving the structural integrity of the building shell.",
    features: ["Drywall & ceiling removal", "Flooring & partition stripping", "Bathroom & kitchen gutting", "Painting & repair preparation"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1622208/img/interior_gutting_demo.png",
    link: "/ServicesPage"
  }];
  return <section data-section-id="62962" id="heavy-lifting-demolition" className="relative py-24 bg-background overflow-hidden dark" ref={ref}>
      {}
      <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#f97316_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary px-4 py-1 text-xs font-mono uppercase tracking-widest rounded-sm">
            S&B PRECISION
          </Badge>
          <h2 className="text-5xl md:text-7xl font-default font-bold text-foreground mb-6 tracking-tighter uppercase">
            HEAVY LIFTING & DEMOLITION
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed font-sans">
            From surgical interior strip-outs to total residential teardowns, SMITH & B GROUP LLC brings industrial-grade power to every square foot. We manage the mess so you can build the future.
          </p>
        </div>

        {}
        <div className="space-y-24">
          {services.map((service, index) => <div key={service.id} data-index={index} className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"} ${index % 2 === 1 ? "lg:flex-row-reverse" : ""}`} style={{
          transitionDelay: `${index * 150}ms`
        }}>
              {}
              <div className="w-full lg:w-1/2 relative group">
                <div className="absolute -inset-2 bg-primary/20 rounded-sm rotate-1 scale-95 group-hover:rotate-0 group-hover:scale-100 transition-transform duration-500" />
                <div className="relative rounded-sm overflow-hidden shadow-none border border-primary/30 aspect-[4/3]">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover grayscale brightness-75 transform group-hover:scale-105 group-hover:grayscale-0 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-80" />
                  <div className="absolute bottom-6 left-6">
                    <div className="bg-primary backdrop-blur-sm p-3 rounded-sm shadow-none inline-flex items-center gap-3 border border-primary">
                      <div className="bg-secondary p-2 rounded-sm text-secondary-foreground">
                        {service.icon}
                      </div>
                      <span className="font-default font-bold text-primary-foreground text-sm uppercase tracking-tight">
                        {service.badge}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {}
              <div className="w-full lg:w-1/2 space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-muted text-foreground text-xs font-mono uppercase tracking-widest border border-border">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    SPEC 0{index + 1}
                  </div>
                  <h3 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight uppercase">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                    {service.description}
                  </p>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-foreground/90 font-sans font-medium uppercase text-sm tracking-wide">{feature}</span>
                    </li>)}
                </ul>

                {}
                {index === 2 && <Card className="mt-8 border-2 border-primary bg-primary/5 rounded-sm overflow-hidden relative">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <ShieldAlert className="w-24 h-24 text-primary" />
                    </div>
                    <CardContent className="p-6 relative z-10">
                      <div className="flex items-start gap-4">
                        <div className="bg-primary text-primary-foreground p-3 rounded-sm shrink-0 shadow-none">
                          <ShieldAlert className="w-6 h-6" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-lg font-default font-bold text-primary uppercase tracking-tight">
                            PROJECT MANAGEMENT ADVISORY
                          </h4>
                          <p className="text-sm text-foreground/80 font-sans leading-relaxed">
                            Expert coordination for complex drywall and painting prep. We ensure all structural removals meet local building codes and safety standards.
                          </p>
                          <div className="pt-2">
                            <Link to="#" className="text-sm font-mono font-bold text-primary uppercase underline decoration-primary decoration-2 underline-offset-4 hover:text-foreground transition-colors">
                              Contact Specialist &rarr;
                            </Link>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>}

                <div className="pt-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-none font-default uppercase tracking-wider transition-all">
                    <Link to={service.link}>
                      <HardHat className="w-4 h-4 mr-2" />
                      {index === 2 ? "Get a Quote" : "View Service"}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}